import { Injectable } from '@angular/core';
import { SplunkRum, SessionBasedSampler } from '@splunk/otel-web';
import { Version } from '../../../environments/app-version';
import { AppConfigService } from '../../services/appConfig/appConfigService';
import { TimberService } from '@logging/timber.service';
import { AppSettings } from '@shared/generalInterfaces';
import { escapeRegExp } from '@shared/utils.service';

/*
 * Service to help manage Splunk RUM (Real User Monitoring) inside microfrontends.
 * We'd like to see all RUM data of microfrontends in the separate dashboards in Splunk.
 * However, SplunkRum is designed as a singleton and it attaches itself to the global window object.
 * This service helps to initialize and deinitialize SplunkRum when microfrontend is loaded and unloaded.
 */
@Injectable({
	providedIn: 'root',
})
export class SplunkHelperService {
	constructor(
		private readonly appConfigService: AppConfigService,
		private timberService: TimberService
	) {}

	init() {
		try {
			const { signalFx } = this.appConfigService.appSettings;

			if (!signalFx.isEnabled && SplunkRum.inited) {
				SplunkRum.deinit();
			}

			if (signalFx.isEnabled && !SplunkRum.inited) {
				const ignoreUrls = this.getIgnoreUrls(this.appConfigService.appSettings);

				SplunkRum.init({
					realm: signalFx.realm,
					rumAccessToken: signalFx.rumAuth,
					applicationName: signalFx.app,
					deploymentEnvironment: signalFx.environment,
					version: Version.version,
					persistence: 'localStorage',
					disableAutomationFrameworks: true,
					disableBots: true,
					ignoreUrls,
					tracer: {
						sampler: new SessionBasedSampler({
							ratio: 0.7,
						}),
					},
					exporter: {
						otlp: true,
					},
				});
			}
		} catch (error) {
			this.timberService.error('Failed to initialize SplunkRum:', { module: 'SplunkHelperService', error });
		}
	}

	destroy() {
		try {
			if (SplunkRum.inited) {
				SplunkRum.deinit();
			}
		} catch (error) {
			this.timberService.error('Failed to deinitialize SplunkRum:', { module: 'SplunkHelperService', error });
		}
	}

	private getIgnoreUrls({ loggingEndpoint, biEndpoint }: AppSettings): RegExp[] {
		const ignoreUrls: RegExp[] = [/\/assets\/[^.]*\.svg$/i, /\.splunkcloud\.com/i, /\.amazonaws\.com/];

		if (loggingEndpoint) {
			ignoreUrls.push(new RegExp(escapeRegExp(loggingEndpoint)));
		}

		if (biEndpoint) {
			ignoreUrls.push(new RegExp(escapeRegExp(biEndpoint)));
		}

		return ignoreUrls;
	}
}
